import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowLeft, Plus, Trash, FloppyDisk, Lightning, DotsSixVertical } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sortable timeline item
const SortableItem = ({ item, idx, presets, isAuto, onUpdate, onRemove, onFlash, onPickChallenge, isSelected, onSelect }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item._sid, disabled: isAuto });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : 1 };
  const ch = presets.find(p => p.id === item.challenge_id) || { title: 'Select', emoji: '?' };

  return (
    <div ref={setNodeRef} style={style} className={`flex items-center gap-2 px-3 py-3.5 rounded-2xl transition-all ${isDragging ? 'shadow-2xl' : ''}`}
      onClick={() => onSelect(idx)}
      data-testid={`timeline-item-${idx}`}
      style={{ ...style, background: isSelected ? 'rgba(108,92,231,0.08)' : 'rgba(255,255,255,0.04)', border: isSelected ? '1px solid rgba(108,92,231,0.3)' : '1px solid rgba(255,255,255,0.08)' }}>

      {/* Drag handle */}
      {!isAuto && (
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-white/20 hover:text-white/50 touch-none" data-testid={`drag-${idx}`}>
          <DotsSixVertical size={20} weight="bold" />
        </div>
      )}
      {isAuto && <div className="w-7" />}

      {/* Time */}
      <input type="time" value={item.time} onChange={e => onUpdate(idx, 'time', e.target.value)}
        disabled={isAuto} className="w-[5.5rem] px-2 py-1.5 rounded-lg bg-white/10 border border-white/10 text-white text-sm text-center disabled:opacity-40 focus:outline-none focus:border-[#B89762]/50" />

      {/* Challenge */}
      <button onClick={(e) => { e.stopPropagation(); if (!isAuto) onPickChallenge(idx); }}
        className="flex-1 flex items-center gap-2.5 min-w-0 text-left">
        <span className="text-xl">{ch.emoji}</span>
        <div className="min-w-0">
          <p className="text-white text-sm font-semibold truncate">{ch.title}</p>
          <p className="text-white/25 text-xs truncate">{item.label}</p>
        </div>
      </button>

      {/* Flash */}
      <button onClick={(e) => { e.stopPropagation(); onFlash(item.challenge_id); }}
        className="p-2 rounded-lg text-[#B89762]/50 hover:text-[#C29D52] hover:bg-[#B89762]/85/10 transition-colors" title="Trigger flash">
        <Lightning size={16} weight="fill" />
      </button>

      {/* Delete */}
      {!isAuto && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(idx); }}
          className="p-2 rounded-lg text-white/15 hover:text-red-400 hover:bg-red-400/10 transition-colors">
          <Trash size={16} weight="bold" />
        </button>
      )}
    </div>
  );
};

const TimelineEditor = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [items, setItems] = useState([]);
  const [timelineMode, setTimelineMode] = useState('auto');
  const [presets, setPresets] = useState([]);
  const [mode, setMode] = useState('wedding');
  const [showPicker, setShowPicker] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        setEvent(r.data);
        const [tRes, pRes] = await Promise.all([
          axios.get(`${API}/events/${r.data.event_id}/challenges/timeline`),
          axios.get(`${API}/challenge-presets/wedding`),
        ]);
        // Add stable IDs for dnd-kit
        setItems((tRes.data.items || []).map((it, i) => ({ ...it, _sid: `item-${i}-${Date.now()}` })));
        setTimelineMode(tRes.data.mode || 'auto');
        setPresets(pRes.data);
      } catch { navigate(-1); }
    })();
  }, [slug]);

  useEffect(() => {
    (async () => { try { const r = await axios.get(`${API}/challenge-presets/${mode}`); setPresets(r.data); } catch {} })();
  }, [mode]);

  const save = async () => {
    if (!event) return;
    setSaving(true);
    try {
      // Strip _sid before saving
      const clean = items.map(({ _sid, ...rest }) => rest);
      await axios.put(`${API}/events/${event.event_id}/challenges/timeline`, { items: clean, mode: timelineMode });
      toast.success('Timeline saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const addItem = () => {
    const lastTime = items.length > 0 ? items[items.length - 1].time : '18:00';
    const [h, m] = lastTime.split(':').map(Number);
    const newM = m + 30 >= 60 ? 0 : m + 30;
    const newH = m + 30 >= 60 ? h + 1 : h;
    setItems(prev => [...prev, {
      _sid: `item-${Date.now()}`,
      time: `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`,
      label: 'New Moment',
      challenge_id: presets[0]?.id || 'w1'
    }]);
  };

  const removeItem = (idx) => { setItems(prev => prev.filter((_, i) => i !== idx)); setSelectedIdx(null); };
  const updateItem = (idx, field, value) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex(i => i._sid === active.id);
    const newIdx = items.findIndex(i => i._sid === over.id);
    setItems(arrayMove(items, oldIdx, newIdx));
  };

  const triggerFlash = async (challengeId) => {
    if (!event) return;
    try { await axios.post(`${API}/events/${event.event_id}/challenges/flash`, { challenge_id: challengeId, duration: 10 }); toast.success('Flash challenge triggered!'); } catch { toast.error('Failed'); }
  };

  if (!event) return <div className="min-h-screen bg-[#15161B] flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#B89762]/50 border-t-transparent" /></div>;

  const isAuto = timelineMode === 'auto';

  return (
    <div className="min-h-screen" style={{ background: '#15161B', fontFamily: 'Inter, sans-serif' }} data-testid="timeline-editor">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <button onClick={() => navigate(`/event/${slug}`)} className="flex items-center gap-2 text-white/60 hover:text-white"><ArrowLeft size={20} weight="bold" /><span className="text-sm">Back</span></button>
        <h1 className="text-white font-bold text-lg">Challenge Timeline</h1>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2 rounded-xl font-bold text-white text-sm hover:scale-105 transition-all" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }} data-testid="save-timeline">
          <FloppyDisk size={16} weight="bold" /> {saving ? '...' : 'Save'}
        </button>
      </div>

      <div className="max-w-xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex rounded-xl overflow-hidden border border-white/10">
            {['auto', 'custom'].map(m => (
              <button key={m} onClick={() => setTimelineMode(m)}
                className={`px-5 py-2.5 text-sm font-semibold transition-all ${timelineMode === m ? 'bg-[#B89762] text-white' : 'bg-[#15161B] text-white/40'}`}>
                {m === 'auto' ? 'Auto Mode' : 'Custom'}
              </button>
            ))}
          </div>
          <select value={mode} onChange={e => setMode(e.target.value)} className="px-4 py-2 rounded-xl bg-white/10 border border-white/10 text-white text-sm">
            <option value="wedding" style={{ color: '#000' }}>Wedding</option>
            <option value="event" style={{ color: '#000' }}>Party</option>
          </select>
        </div>

        {isAuto && (
          <div className="rounded-2xl p-4 mb-6 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <p className="text-white/50 text-sm">Auto Mode uses a smart default timeline. Switch to Custom to drag, reorder, and edit.</p>
          </div>
        )}

        {/* DND Timeline */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map(i => i._sid)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2.5" data-testid="timeline-items">
              {items.map((item, idx) => (
                <SortableItem key={item._sid} item={item} idx={idx} presets={presets} isAuto={isAuto}
                  onUpdate={updateItem} onRemove={removeItem} onFlash={triggerFlash}
                  onPickChallenge={setShowPicker} isSelected={selectedIdx === idx} onSelect={setSelectedIdx} />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {!isAuto && (
          <button onClick={addItem} className="w-full mt-4 py-4 rounded-2xl font-semibold text-white/30 text-sm flex items-center justify-center gap-2 border-2 border-dashed border-white/10 hover:border-[#C29D52]/30 hover:text-[#C29D52] transition-colors" data-testid="add-moment">
            <Plus size={16} weight="bold" /> Add Moment
          </button>
        )}
      </div>

      {/* Challenge Picker */}
      {showPicker !== null && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.8)' }} onClick={() => setShowPicker(null)}>
          <div className="w-full max-w-lg rounded-t-3xl p-6 max-h-[70vh] overflow-y-auto" style={{ background: '#1a1a1e' }} onClick={e => e.stopPropagation()} data-testid="challenge-picker">
            <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mb-4" />
            <h3 className="text-white font-bold text-lg mb-4">Choose a challenge</h3>
            <div className="space-y-1.5">
              {presets.map(p => (
                <button key={p.id} onClick={() => { updateItem(showPicker, 'challenge_id', p.id); setShowPicker(null); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#15161B] transition-colors text-left">
                  <span className="text-2xl">{p.emoji}</span>
                  <div>
                    <p className="text-white text-sm font-semibold">{p.title}</p>
                    <p className="text-white/25 text-xs">{p.category}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineEditor;
